// src/utils/silentResetMigration.js
//
// Fila persistida (1 slot) da migração de pagamento do "silent channel reset".
//
// Caso Rodrigo Guimaraes (10/07/2026): o reset gerou patientId novo com
// paymentConfirmed local, mas o backend continuou com o pagamento no id
// antigo — painel do médico "Pendente" e atendimento no canal errado.
//
// O reset acontece justamente em cenários de rede ruim, então o POST pode
// falhar: a migração pendente fica em localStorage e é re-tentada no boot
// do ConsultaChat até o backend responder (ok:true aplica; ok:false com
// reason significa rejeição de negócio — ex.: id antigo nunca pagou — e
// também remove da fila, não adianta re-tentar).

import { logUxEvent } from '../telemetry/uxLogger';
import { clearPaymentConfirmation, isPaymentConfirmedFor } from './paymentFlag';

const PENDING_KEY = 'pendingSilentResetMigration';
const DONE_KEY = 'silentResetDoneFor';
const MAX_PENDING_AGE_MS = 72 * 3600e3; // mesma janela do backend

// `old_payment_not_found` também vem para quem pagou por alias (pagamento
// gravado em outro id). A marca de pago do id novo só cai depois que
// /payments/status do id antigo, que resolve alias, confirmar que não há
// pagamento.
const UNPAID_REJECTIONS = new Set(['old_payment_not_found', 'old_payment_not_paid']);

const clearSilentResetDone = () => {
  try { localStorage.removeItem(DONE_KEY); } catch {}
};

/**
 * Um reset por sessão paga (caso Marcos Danyel 28/08/2026: 2 resets em 4 min
 * geraram 3 canais e republicaram anamnese+conduta). A flag vive no LS até
 * o fim da consulta / novo cadastro; janela 72h casa com o backend.
 */
export function hasSilentResetAlreadyRun() {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    if (!p || !p.originPatientId || !p.doneAt) { clearSilentResetDone(); return false; }
    if (Date.now() - Number(p.doneAt) > MAX_PENDING_AGE_MS) { clearSilentResetDone(); return false; }
    return true;
  } catch {
    clearSilentResetDone();
    return false;
  }
}

/** Marca que esta sessão paga já usou o unique hop de silent reset. */
export function markSilentResetDone(originPatientId) {
  const origin = String(originPatientId || '').trim();
  if (!origin) return false;
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify({
      originPatientId: origin,
      doneAt: Date.now(),
    }));
    return true;
  } catch {
    return false;
  }
}

const readIdentity = () => {
  const get = (k) => {
    try {
      return localStorage.getItem(k) || sessionStorage.getItem(k) || '';
    } catch {
      return '';
    }
  };
  return {
    email: get('patientEmail'),
    cpf: get('patientCpf'),
    phone: get('patientPhone'),
  };
};

/** Registra a migração pendente. Chamar no momento do reset (antes do POST). */
export function enqueueSilentResetMigration({ oldPatientId, newPatientId }) {
  if (!oldPatientId || !newPatientId || oldPatientId === newPatientId) return false;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({
      oldPatientId: String(oldPatientId),
      newPatientId: String(newPatientId),
      createdAt: Date.now(),
    }));
    return true;
  } catch {
    return false;
  }
}

const clearPending = () => {
  try { localStorage.removeItem(PENDING_KEY); } catch {}
};

const readPending = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !p.oldPatientId || !p.newPatientId) { clearPending(); return null; }
    if (Date.now() - Number(p.createdAt || 0) > MAX_PENDING_AGE_MS) { clearPending(); return null; }
    return p;
  } catch {
    clearPending();
    return null;
  }
};

let inFlight = false;

/** Na dúvida (rede, HTTP de erro, corpo inválido), responde false. */
async function oldPatientIdIsUnpaid(baseUrl, oldPatientId) {
  try {
    const res = await fetch(
      `${baseUrl}/payments/status?userId=${encodeURIComponent(oldPatientId)}`,
    );
    if (!res || res.status < 200 || res.status >= 300) return false;
    const data = await res.json();
    return !!data && data.paid !== true;
  } catch {
    return false;
  }
}

/**
 * Envia a migração pendente (se houver) ao backend. Seguro chamar em qualquer
 * boot — no-op sem pendência. Nunca lança.
 *
 * Se o backend recusar porque o id antigo não tinha pagamento, a marca de
 * pago do id novo é apagada e o retorno é 'rejected_unpaid'.
 *
 * @param {string} baseUrl BASE_URL da API
 * @returns {Promise<'applied'|'rejected'|'rejected_unpaid'|'retry_later'|'none'>}
 */
export async function flushSilentResetMigration(baseUrl) {
  const pending = readPending();
  if (!pending || !baseUrl || inFlight) return 'none';
  inFlight = true;
  try {
    const identity = readIdentity();
    const res = await fetch(`${baseUrl}/payments/silent-reset-migration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        oldPatientId: pending.oldPatientId,
        newPatientId: pending.newPatientId,
        email: identity.email || undefined,
        cpf: identity.cpf || undefined,
        phone: identity.phone || undefined,
      }),
    });
    if (res.status >= 500) return 'retry_later';
    const data = await res.json().catch(() => null);
    if (!data) return 'retry_later';
    clearPending();
    let flagCleared = false;
    if (
      data.ok !== true &&
      UNPAID_REJECTIONS.has(data.reason) &&
      isPaymentConfirmedFor(pending.newPatientId) &&
      (await oldPatientIdIsUnpaid(baseUrl, pending.oldPatientId))
    ) {
      clearPaymentConfirmation();
      flagCleared = true;
    }
    try {
      logUxEvent('silent_reset_migration_result', {
        applied: data.ok === true && data.applied === true,
        already_migrated: data.alreadyMigrated === true,
        reason: data.reason || null,
        flag_cleared: flagCleared,
        old_patient_id: String(pending.oldPatientId).slice(0, 80),
      });
    } catch {}
    if (flagCleared) return 'rejected_unpaid';
    return data.ok === true ? 'applied' : 'rejected';
  } catch {
    // Rede indisponível (cenário esperado pós-reset) — mantém na fila.
    return 'retry_later';
  } finally {
    inFlight = false;
  }
}

export const SILENT_RESET_MIGRATION_PENDING_KEY = PENDING_KEY;
export const SILENT_RESET_DONE_KEY = DONE_KEY;
